import { apiClient } from '@/lib/apiClient';
import { offlineDb, type VendaPendente } from './offlineDb';

// ── Tipos de resposta do endpoint sync-offline ────────────────────────────────

interface SyncResultado {
  processadas: string[];
  conflitos:   { idLocal: string; motivo: string }[];
  erros:       { idLocal: string; mensagem: string }[];
}

// ── Fila interna para evitar sync simultâneo ──────────────────────────────────

let _sincronizando = false;
const _listeners: Array<(pendentes: number, conflitos: number) => void> = [];

export function onSyncStatusChange(cb: (pendentes: number, conflitos: number) => void) {
  _listeners.push(cb);
  return () => {
    const i = _listeners.indexOf(cb);
    if (i >= 0) _listeners.splice(i, 1);
  };
}

async function _notificar() {
  const pendentes = await offlineDb.contarPendentes();
  const conflitos = await offlineDb.contarConflitos();
  _listeners.forEach(fn => fn(pendentes, conflitos));
}

// ── Sincronizar via endpoint batch ────────────────────────────────────────────

const SYNC_LOCK = 'pdv-sync-offline';

/**
 * Sincroniza as vendas offline pendentes.
 *
 * PDV-06: coordena entre ABAS. O guard de módulo `_sincronizando` só protege
 * re-entrância dentro de UMA aba — duas abas abertas têm contextos JS separados
 * sobre o mesmo IndexedDB e ambas leriam as pendentes e fariam POST do mesmo
 * lote. A Web Locks API serializa o sync por origem: enquanto uma aba detém o
 * lock, as demais simplesmente pulam (`ifAvailable`) e tentam no próximo gatilho.
 * (A idempotência por idLocal no servidor — índice único + tratamento de corrida —
 * continua sendo a garantia final; o lock apenas evita o trabalho redundante.)
 */
export async function sincronizarVendasPendentes(): Promise<void> {
  if (!navigator.onLine) return;

  const locks = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: LockManager }).locks
    : undefined;

  if (locks) {
    await locks.request(SYNC_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock) return; // outra aba já está sincronizando
      await _executarSync();
    });
  } else {
    // Browsers sem Web Locks: cai no guard de módulo (protege ao menos a re-entrância na aba).
    await _executarSync();
  }
}

async function _executarSync(): Promise<void> {
  if (_sincronizando) return;

  _sincronizando = true;
  try {
    const todas = await offlineDb.obterVendasPendentes();

    // Recovery: vendas em "Sincronizando" são órfãs de um sync interrompido
    // (refresh/fechamento da aba com o POST em voo) — com o lock _sincronizando
    // em mãos, nenhum sync está em andamento. Reenvio é seguro: o servidor é
    // idempotente por idLocal.
    const orfas = todas.filter(v => v.status === 'Sincronizando');
    await Promise.all(orfas.map(v =>
      offlineDb.atualizarStatusVenda(v.idLocal, 'Pendente')
    ));
    orfas.forEach(v => { v.status = 'Pendente'; });

    const pendentes = todas.filter(v => v.status === 'Pendente');
    if (pendentes.length === 0) return;

    // Marca todas como "Sincronizando"
    await Promise.all(pendentes.map(v =>
      offlineDb.atualizarStatusVenda(v.idLocal, 'Sincronizando')
    ));
    await _notificar();

    const payload = pendentes.map(v => ({
      idLocal:     v.idLocal,
      empresaId:   v.empresaId,
      caixaId:     v.caixaId,
      clienteId:   v.clienteId,
      itens:       v.itens,
      pagamentos:  v.pagamentos,
      dtCriacao:   v.dtCriacao,
    }));

    const { data } = await apiClient.post<SyncResultado>('/api/pdv/sync-offline', { vendas: payload });

    // Processadas com sucesso → remove da fila
    await Promise.all(data.processadas.map(id => offlineDb.removerVendaPendente(id)));

    // Conflitos → marca para revisão
    await Promise.all(data.conflitos.map(c =>
      offlineDb.atualizarStatusVenda(c.idLocal, 'ConflitoPendente', c.motivo)
    ));

    // Erros técnicos → volta para Pendente (tentará novamente)
    await Promise.all(data.erros.map(e =>
      offlineDb.atualizarStatusVenda(e.idLocal, 'Pendente', e.mensagem)
    ));

  } catch {
    // Falha de rede — mantém "Pendente" para tentar novamente
    const todas = await offlineDb.obterVendasPendentes();
    await Promise.all(
      todas
        .filter(v => v.status === 'Sincronizando')
        .map(v => offlineDb.atualizarStatusVenda(v.idLocal, 'Pendente'))
    );
  } finally {
    _sincronizando = false;
    await _notificar();
  }
}

// ── Salvar venda offline ──────────────────────────────────────────────────────

export async function salvarVendaOffline(venda: Omit<VendaPendente, 'tentativas' | 'status' | 'dtCriacao'>): Promise<string> {
  const entry: VendaPendente = {
    ...venda,
    dtCriacao: new Date().toISOString(),
    tentativas: 0,
    status: 'Pendente',
  };
  await offlineDb.salvarVendaPendente(entry);
  await _notificar();
  return entry.idLocal;
}

// ── Descartar venda da fila ───────────────────────────────────────────────────

export async function descartarVendaPendente(idLocal: string): Promise<void> {
  await offlineDb.removerVendaPendente(idLocal);
  await _notificar();
}

// ── Registrar listener online/offline ────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    sincronizarVendasPendentes().catch(() => undefined);
  });
}
